
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Edit, Trash2, Eye, Target, Calendar, Clock, User, Building2 } from "lucide-react";
import { PerformanceGoal } from "./services/performanceService";
import { AddGoalDialog } from "./AddGoalDialog";
import { EditGoalDialog } from "./EditGoalDialog";
import { DeleteGoalDialog } from "./DeleteGoalDialog";

interface GoalsTableProps {
  goals: PerformanceGoal[];
  onRefresh: () => void;
  selectedTeamMemberId?: string; // Add this prop to support team member context
}

export const GoalsTable = ({
  goals,
  onRefresh,
  selectedTeamMemberId
}: GoalsTableProps) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState<PerformanceGoal | null>(null);
  const [deletingGoal, setDeletingGoal] = useState<PerformanceGoal | null>(null);

  // Check if any goals are frozen to disable Add button
  const hasGoals = goals.length > 0;
  const areGoalsFrozen = hasGoals && goals.every(goal => goal.is_frozen);
  
  // Sort goals alphabetically by goal title
  const sortedGoals = [...goals].sort((a, b) => a.goal_title.localeCompare(b.goal_title));
  
  const handleGoalUpdated = async () => {
    await onRefresh(); // This will trigger the parent to refetch data and update the cards
    setEditingGoal(null);
  };
  
  const handleGoalAdded = async () => {
    await onRefresh(); // This will trigger the parent to refetch data and update the cards
    setShowAddDialog(false);
  };
  
  const handleGoalDeleted = async () => {
    await onRefresh(); // This will trigger the parent to refetch data and update the cards
    setDeletingGoal(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'active': {
        color: 'bg-green-500/20 text-green-300 text-[9px] border-green-500/50',
        label: 'Active'
      },
      'paused': {
        color: 'bg-yellow-500/20 text-yellow-300 text-[9px] border-yellow-500/50',
        label: 'Paused'
      },
      'completed': {
        color: 'bg-blue-500/20 text-blue-300 text-[9px] border-blue-500/50',
        label: 'Completed'
      },
      'cancelled': {
        color: 'bg-red-500/20 text-red-300 text-[9px] border-red-500/50',
        label: 'Cancelled'
      }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      'high': {
        color: 'bg-red-500/20 text-red-300 text-[9px] border-red-500/50',
        label: 'High'
      },
      'medium': {
        color: 'bg-yellow-500/20 text-yellow-300 text-[9px] border-yellow-500/50',
        label: 'Medium'
      },
      'low': {
        color: 'bg-green-500/20 text-green-300 text-[9px] border-green-500/50',
        label: 'Low'
      }
    };
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getCategoryBadge = (category: string) => {
    const categoryConfig = {
      'financial': {
        color: 'bg-emerald-500/20 text-emerald-300 text-[9px] border-emerald-500/50',
        label: 'Financial'
      },
      'project_delivery': {
        color: 'bg-blue-500/20 text-blue-300 text-[9px] border-blue-500/50',
        label: 'Project Delivery'
      },
      'customer_impact': {
        color: 'bg-purple-500/20 text-purple-300 text-[9px] border-purple-500/50',
        label: 'Customer Impact'
      },
      'innovation': {
        color: 'bg-orange-500/20 text-orange-300 text-[9px] border-orange-500/50',
        label: 'Innovation'
      },
      'leadership_behaviors': {
        color: 'bg-pink-500/20 text-pink-300 text-[9px] border-pink-500/50',
        label: 'Leadership'
      }
    };
    const config = categoryConfig[category as keyof typeof categoryConfig] || categoryConfig.financial;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 90) return 'text-green-400 text-[9px]';
    if (progress >= 70) return 'text-yellow-400 text-[9px]';
    if (progress >= 50) return 'text-orange-400 text-[9px]';
    return 'text-red-400';
  };

  return (
    <Card className="bg-secondary/50 border-0">
      <CardHeader className="py-2">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              S.M.A.R.T. Goals ({goals.length})
              {areGoalsFrozen && hasGoals && (
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/50 text-[9px]">
                  🔒 Frozen
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-gray-300">
              Track and manage your performance objectives
            </CardDescription>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="bg-primary hover:bg-primary/90 text-black" disabled={areGoalsFrozen}>
            <Plus className="h-4 w-4 mr-2" />
            {areGoalsFrozen ? "Goals Frozen" : "Add New Goal"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {goals.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-primary/20 hover:bg-secondary/30">
                  <TableHead className="text-primary text-[9px]">Goal Title</TableHead>
                  <TableHead className="text-primary text-[9px]">Category</TableHead>
                  <TableHead className="text-primary text-[9px]">Type</TableHead>
                  <TableHead className="text-primary text-[9px]">Status</TableHead>
                  <TableHead className="text-primary text-[9px]">Priority</TableHead>
                  <TableHead className="text-primary text-[9px]">Target Value</TableHead>
                  <TableHead className="text-primary text-[9px]">Progress</TableHead>
                  <TableHead className="text-primary text-[9px]">Target Date</TableHead>
                  <TableHead className="text-primary text-[9px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedGoals.map(goal => (
                  <TableRow key={goal.id} className="border-primary/10 hover:bg-secondary/20">
                    <TableCell className="text-white text-[9px]">
                      <div className="flex flex-col text-[9px]">
                        <span className="font-medium">{goal.goal_title}</span>
                        {goal.goal_description && (
                          <span className="text-[9px] text-gray-400 mt-1 line-clamp-2">{goal.goal_description}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getCategoryBadge(goal.goal_category)}</TableCell>
                    <TableCell>
                      <Badge className="bg-indigo-500/20 text-indigo-300 text-[9px] border-indigo-500/50">
                        {goal.goal_type === 'performance' ? 'Performance' : 'Behavioral'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(goal.status)}</TableCell>
                    <TableCell>{getPriorityBadge(goal.priority)}</TableCell>
                    <TableCell className="text-gray-300 text-[9px]">
                      {goal.target_value || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-[9px]">
                        <span className={`text-[9px] ${getProgressColor(goal.current_progress)}`}>
                          {goal.current_progress}%
                        </span>
                        <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden text-[9px]">
                          <div className={`h-full transition-all duration-300 text-[9px] ${goal.current_progress >= 90 ? 'bg-green-400' : goal.current_progress >= 70 ? 'bg-yellow-400' : goal.current_progress >= 50 ? 'bg-orange-400' : 'bg-red-400'}`} style={{ width: `${Math.min(goal.current_progress, 100)}%` }} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-300 text-[9px]">
                      <div className="flex items-center gap-1 text-[9px]">
                        {formatDate(goal.target_date)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-[9px]">
                      <div className="flex justify-end gap-1 text-[9px]">
                        <Button variant="ghost" size="sm" onClick={() => setEditingGoal(goal)} className="h-7 w-7 p-0 text-primary-light hover:text-white hover:bg-primary/20">
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeletingGoal(goal)} className="h-7 w-7 p-0 text-red-400 hover:text-red-400 hover:bg-red-500/20" disabled={goal.is_frozen}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-white text-[9px] font-medium mb-2">No performance goals yet</h3>
            <p className="text-gray-400 text-[9px] mb-4">Start by adding your first performance goal to track your objectives.</p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-primary hover:bg-primary/90 text-black" disabled={areGoalsFrozen}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Goal
            </Button>
          </div>
        )}
      </CardContent>

      <AddGoalDialog 
        open={showAddDialog} 
        onOpenChange={setShowAddDialog} 
        onGoalAdded={handleGoalAdded} 
        selectedTeamMemberId={selectedTeamMemberId}
      />

      {editingGoal && (
        <EditGoalDialog goal={editingGoal} open={!!editingGoal} onOpenChange={open => !open && setEditingGoal(null)} onGoalUpdated={handleGoalUpdated} />
      )}

      {deletingGoal && (
        <DeleteGoalDialog goal={deletingGoal} open={!!deletingGoal} onOpenChange={open => !open && setDeletingGoal(null)} onGoalDeleted={handleGoalDeleted} />
      )}
    </Card>
  );
};
